import {Box, Typography} from '@mui/material';
import styles from '../../../styles/components/clientdetails/projectDrawerStyle.module.scss';
import {t} from 'i18next';
import SectionDetailsHeader from './SectionDetailsHeader';
import {AurTextField} from '../../common/aur-text-field/AurTextField';
import {ProjectFields} from '../../../types/project-type/projectDetailsType';
import {EditFlag} from './ProjectDrawer';
import {useEffect, useState} from 'react';
import {useModal} from '../../../contexts/modalContext/ModalContext';
import {
  CHAR_LIMIT_500,
  PROJECT_ID_LIMIT_40,
} from '../../../constants/constants';

type ClientInformationProps = {
  entityName: string;
  clientUniqueProjectId: string;
  isEdit?: boolean;
  saveChangesClickHandler: (projectFields: ProjectFields) => Promise<boolean>;
  editClickHandler: (editFlag: Partial<EditFlag>) => void;
  cancelClickHandler: (editFlag: Partial<EditFlag>) => void;
  internalProjectName: string;
  hasEditProjectProfilePerm?: boolean;
};

type ClientInfo = {
  entityName: string;
  clientUniqueProjectId: string;
  internalProjectName: string;
};

const ProjectClientInformation: React.FC<ClientInformationProps> = ({
  entityName,
  clientUniqueProjectId,
  internalProjectName,
  isEdit = false,
  hasEditProjectProfilePerm,
  saveChangesClickHandler,
  editClickHandler,
  cancelClickHandler,
}) => {
  const [clientInformation, setClientInformation] = useState<ClientInfo>({
    entityName: entityName,
    clientUniqueProjectId: clientUniqueProjectId,
    internalProjectName,
  });
  const [isClientNameValid, setIsClientNameValid] = useState(true);
  const [isUniqueProjIdValid, setIsUniqueProjIdValid] = useState(true);
  const {showModal} = useModal();
  const [saveButtonDisabled, setSaveButtonDisabled] = useState(true);
  const [isInternalProjectNameValid, setIsInternalProjectNameValid] =
    useState(true);

  useEffect(() => {
    if (isEdit) {
      setClientInformation({
        entityName,
        clientUniqueProjectId,
        internalProjectName,
      });

      setIsUniqueProjIdValid(true);
      setIsClientNameValid(true);
      setIsInternalProjectNameValid(true);
    }
  }, [isEdit, entityName, clientUniqueProjectId, internalProjectName]);

  const isValidInput = (inputVal: string, limit: number = CHAR_LIMIT_500) => {
    return inputVal.trim().length <= limit;
  };

  const onEditClickHandler = () => {
    editClickHandler({clientInformation: true});
  };
  const hasLocalChanges = () => {
    if (
      clientInformation.entityName != entityName ||
      clientInformation.clientUniqueProjectId != clientUniqueProjectId ||
      clientInformation.internalProjectName !== internalProjectName
    ) {
      return true;
    } else {
      return false;
    }
  };
  const handleCancelClick = () => {
    setClientInformation({
      entityName: entityName,
      clientUniqueProjectId: clientUniqueProjectId,
      internalProjectName,
    });
    cancelClickHandler({clientInformation: false});

    setIsClientNameValid(true);
    setIsUniqueProjIdValid(true);
  };
  const onCancelClickHandler = () => {
    if (hasLocalChanges()) {
      showModal({
        title: t(
          'clientDetailsPage.projectDrawer.modal.closePanelUnsavedChangePopup.title'
        ),
        description: t(
          'clientDetailsPage.projectDrawer.modal.closePanelUnsavedChangePopup.description'
        ),
        labelOkButton: t(
          'clientDetailsPage.projectDrawer.modal.closePanelUnsavedChangePopup.okButtonLabel'
        ),
        labelCancelButton: t(
          'clientDetailsPage.projectDrawer.modal.closePanelUnsavedChangePopup.cancelButtonLabel'
        ),
        onOk: () => {
          handleCancelClick();
        },
      });
    } else {
      handleCancelClick();
    }
  };
  const onSaveChangeHandler = async () => {
    if (
      isValidInput(clientInformation.entityName) &&
      isValidInput(clientInformation.clientUniqueProjectId) &&
      isValidInput(clientInformation.internalProjectName, PROJECT_ID_LIMIT_40)
    ) {
      const isSavedSuccessfullly = await saveChangesClickHandler({
        entityName: clientInformation.entityName,
        clientUniqueProjectId: clientInformation.clientUniqueProjectId,
        internalProjectName: clientInformation.internalProjectName,
      });

      setSaveButtonDisabled(isSavedSuccessfullly);
    }
  };

  const validateInput = (
    entityNameVal: string,
    uniqueProjIdVal: string,
    internalProjectNameVal: string
  ) => {
    let invalidFlag = false;

    if (!isValidInput(entityNameVal)) {
      setIsClientNameValid(false);
      invalidFlag = true;
    } else {
      setIsClientNameValid(true);
    }

    // Existing validation - 40 chars
    if (!isValidInput(uniqueProjIdVal, PROJECT_ID_LIMIT_40)) {
      setIsUniqueProjIdValid(false);
      invalidFlag = true;
    } else {
      setIsUniqueProjIdValid(true);
    }

    // internal project name field validation - 40 chars
    if (!isValidInput(internalProjectNameVal, PROJECT_ID_LIMIT_40)) {
      setIsInternalProjectNameValid(false);
      invalidFlag = true;
    } else {
      setIsInternalProjectNameValid(true);
    }

    if (invalidFlag) {
      setSaveButtonDisabled(true);
      return;
    }

    if (
      entityName !== entityNameVal ||
      clientUniqueProjectId !== uniqueProjIdVal ||
      internalProjectName !== internalProjectNameVal
    ) {
      setSaveButtonDisabled(false);
    } else {
      setSaveButtonDisabled(true);
    }
  };

  const onEntityNameChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setClientInformation(() => {
      return {...clientInformation, entityName: e.target.value};
    });

    validateInput(
      e.target.value.trim(),
      clientInformation.clientUniqueProjectId.trim(),
      clientInformation.internalProjectName.trim()
    );
  };

  const onInternalProjectNameChange = (
    e: React.ChangeEvent<HTMLInputElement>
  ) => {
    setClientInformation(() => ({
      ...clientInformation,
      internalProjectName: e.target.value,
    }));

    validateInput(
      clientInformation.entityName.trim(),
      clientInformation.clientUniqueProjectId.trim(),
      e.target.value.trim()
    );
  };

  const onClientUniqueProjectIdChange = (
    e: React.ChangeEvent<HTMLInputElement>
  ) => {
    setClientInformation(() => {
      return {...clientInformation, clientUniqueProjectId: e.target.value};
    });

    validateInput(
      clientInformation.entityName.trim(),
      e.target.value.trim(),
      clientInformation.internalProjectName.trim()
    );
  };

  const viewMode = (
    <Box className={`${styles.projectDetailsClientInfoBody}`}>
      <Box className={`${styles.clientInfoColumnFlex}`}>
        <Typography
          className={`typo-text subhead4_size ${styles.clientInfoColunm}`}
        >
          {t('clientDetailsPage.projectDrawer.entityName')}
        </Typography>

        <Typography
          className={`typo-text body2_size ${styles.clientInfoColunmValue}`}
        >
          {entityName?.trim() || '--'}
        </Typography>
      </Box>
      <Box className={`${styles.clientInfoColumnFlex}`}>
        <Typography
          className={`typo-text subhead4_size ${styles.clientInfoColunm}`}
        >
          {t('welcomeScreenSingleClient.InternalProjectName')}
        </Typography>

        <Typography
          className={`typo-text body2_size ${styles.clientInfoColunmValue}`}
        >
          {internalProjectName?.trim() || '--'}
        </Typography>
      </Box>
      <Box className={`${styles.clientInfoColumnFlex}`}>
        <Typography
          className={`typo-text subhead4_size ${styles.clientInfoColunm}`}
        >
          {t('clientDetailsPage.projectDrawer.clientUniqueProjectId')}
        </Typography>

        <Typography
          className={`typo-text body2_size ${styles.clientInfoColunmValue}`}
        >
          {clientUniqueProjectId?.trim() || '--'}
        </Typography>
      </Box>
    </Box>
  );

  const editMode = (
    <Box className={`${styles.projectDrawerSectionEditContainer}`}>
      <Box className={`${styles.projectDetailsTextField}`}>
        <AurTextField
          error={!isClientNameValid}
          variant="filled"
          label={t('clientDetailsPage.projectDrawer.entityName')}
          value={clientInformation.entityName}
          className={`aur-textfield custom-text-field ${styles.dropdown} ${styles.fieldFocused} ${styles.inputFieldTypography}`}
          onChange={onEntityNameChange}
          ariaLabel={t('clientDetailsPage.projectDrawer.entityName')}
          helperText={
            isClientNameValid
              ? t('clientDetailsPage.projectDrawer.charLimitValidHelperText')
              : t(
                  'clientDetailsPage.projectDrawer.CharLimitExceedErrorHelperText'
                )
          }
        />
      </Box>
      <Box className={`${styles.projectDetailsTextField}`}>
        <AurTextField
          error={!isInternalProjectNameValid}
          variant="filled"
          label={t('welcomeScreenSingleClient.InternalProjectName')}
          value={clientInformation.internalProjectName}
          className={`aur-textfield custom-text-field ${styles.dropdown} ${styles.fieldFocused} ${styles.inputFieldTypography}`}
          onChange={onInternalProjectNameChange}
          ariaLabel={t('welcomeScreenSingleClient.InternalProjectName')}
          helperText={t(
            'clientDetailsPage.projectDrawer.projectIdCharLimitValidHelperText'
          )}
        />
      </Box>
      <Box className={`${styles.projectDetailsTextField}`}>
        <AurTextField
          error={!isUniqueProjIdValid}
          variant="filled"
          label={t('clientDetailsPage.projectDrawer.clientUniqueProjectId')}
          value={clientInformation.clientUniqueProjectId}
          className={`aur-textfield custom-text-field ${styles.dropdown} ${styles.fieldFocused} ${styles.inputFieldTypography}`}
          onChange={onClientUniqueProjectIdChange}
          ariaLabel={t('clientDetailsPage.projectDrawer.clientUniqueProjectId')}
          helperText={t(
            'clientDetailsPage.projectDrawer.projectIdCharLimitValidHelperText'
          )}
        />
      </Box>
    </Box>
  );

  return (
    <Box className={`${styles.projectDetailsClientInfoContainer}`}>
      <Box className={`${styles.projectDetailsClientInfoHeader}`}>
        <SectionDetailsHeader
          headerText={t(
            'clientDetailsPage.projectDrawer.clientInfoSectionTitle'
          )}
          onCancelHandler={onCancelClickHandler}
          onEditHandler={onEditClickHandler}
          onSaveChangesHandler={onSaveChangeHandler}
          isEdit={isEdit}
          isEditButtonVisible={hasEditProjectProfilePerm}
          isSaveButtonDisabled={saveButtonDisabled}
        />
      </Box>

      {isEdit ? editMode : viewMode}
    </Box>
  );
};

export default ProjectClientInformation;
